import { FC, useState } from "react";
//#region types
import { BenefitsEnums } from "models/enums";
import { CurrencyCode } from "models/enums/currency";
import {
  ChoiceProps,
  CurrencyInputProps,
  DateInputProps,
  SearchInputProps,
  TextInputProps,
  ToggleProps,
} from "models/propTypes/wintFormPropsTypes";
import { CarBenefitSectionProps } from "models/propTypes/benefitsPropTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { TranslationKeys } from "translations";
import { FieldConstants } from "utils/constants";
//#endregion
//#region components
import {
  Choice,
  CurrencyInput,
  DateInput,
  HorizontalFormField,
  SearchInput,
  TextInput,
  Toggle,
  VerticalFormField,
} from "components/common/wintForms";
import useBenefitTypesHelper from "../forms/useBenefitTypesHelper";
import { useBenefitService } from "serviceHooks";
import { useDebouncedCallback } from "use-debounce";
//#endregion
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";

const CarBenefitSection: FC<CarBenefitSectionProps> = (props) => {
  const { connect, yearList, brandList, setValues, values } = props;

  const { getVehicleList, getBenefitAmount } = useBenefitService();

  const [vehicleList, setVehicleList] = useState<AutocompleteListItem[]>([]);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(false);

  const carBenefitSearchMethodConnect = connect("carBenefitData.searchMethod");
  const searchMethod = carBenefitSearchMethodConnect.value as BenefitsEnums.CarBenefitSearchMethod;

  const yearConnect = connect("carBenefitData.year");
  const brandConnect = connect("carBenefitData.brand");
  const vehicleConnect = connect("carBenefitData.vehicle");
  const modelNumberConnect = connect("carBenefitData.modelNumber");
  const extraEquipmentCostConnect = connect("carBenefitData.costOfExtraEquipment");
  const annualTaxConnect = connect("carBenefitData.annualTax");
  const isAnnual10KmMileageMoreThan3000Connect = connect("carBenefitData.isAnnual10KmMileageMoreThan3000");
  const dateOfRegistrationConnect = connect("carBenefitData.dateOfRegistration");
  const benefitAmountConnect = connect("benefitValue");
  const benefitStartDateConnect = connect("startDate");

  const { carBenefitSearchMethodChoices } = useBenefitTypesHelper();

  const fetchVehicleList = async (year: number, brand: string) => {
    if (year && brand) {
      setIsLoadingVehicles(true);
      try {
        const vehicles = await getVehicleList(searchMethod, year, brand);
        setVehicleList(vehicles);
      } catch (error) {
        console.error("Failed to fetch vehicle list:", error);
        setVehicleList([]);
      } finally {
        setIsLoadingVehicles(false);
      }
    } else {
      setVehicleList([]);
      setIsLoadingVehicles(false);
    }
  };

  const onSearchMethodChange = (searchMethod: BenefitsEnums.CarBenefitSearchMethod, shouldValidate?: boolean) => {
    carBenefitSearchMethodConnect.setValue(searchMethod, shouldValidate);
    setValues(
      {
        ...values,
        benefitValue: undefined,
        carBenefitData: {
          searchMethod: searchMethod,
          isAnnual10KmMileageMoreThan3000: false,
        },
      },
      shouldValidate
    );
  };

  const onYearChange = async (year: AutocompleteListItem, shouldValidate?: boolean) => {
    yearConnect.setValue(year, shouldValidate);
    vehicleConnect.setValue(undefined, shouldValidate);
    const brand = brandConnect.value?.value as string;
    if (year && brand) {
      await fetchVehicleList(year.id as number, brand);
    }
  };

  const onBrandChange = async (brand: AutocompleteListItem, shouldValidate?: boolean) => {
    brandConnect.setValue(brand, shouldValidate);
    vehicleConnect.setValue(undefined, shouldValidate);
    const year = yearConnect.value?.id as number | undefined;
    if (year && brand) {
      await fetchVehicleList(year, brand.value as string);
    }
  };

  const onVehicleSelect = async (vehicle: AutocompleteListItem, shouldValidate?: boolean) => {
    vehicleConnect.setValue(vehicle, shouldValidate);
    const modelNumber = vehicle.data.modelCode as string;
    modelNumberConnect.setValue(modelNumber, shouldValidate);
    recalculateBenefitAmount({ modelNumber });
  };

  const recalculateBenefitAmount = useDebouncedCallback(
    (overrides?: {
      annualTax?: number;
      extraCost?: number;
      isAnnual10KmMileageMoreThan3000?: boolean;
      dateOfRegistration?: Date;
      modelNumber?: string;
    }) => {
      const benefitStartDate = benefitStartDateConnect.value as Date;
      const annualTax = overrides?.annualTax ?? (annualTaxConnect.value as number | undefined);
      const modelNumber = overrides?.modelNumber ?? (modelNumberConnect.value as string | undefined);
      const taxationYear = benefitStartDate
        ? DateHelper.getYear(benefitStartDate)
        : DateHelper.getYear(DateHelper.today());
      const extraCost = overrides?.extraCost ?? (extraEquipmentCostConnect.value as number | undefined);
      const isAnnual10KmMileageMoreThan3000 =
        overrides?.isAnnual10KmMileageMoreThan3000 ??
        (isAnnual10KmMileageMoreThan3000Connect.value as boolean | undefined);
      const dateOfRegistration = overrides?.dateOfRegistration ?? (dateOfRegistrationConnect.value as Date | undefined);

      if (
        annualTax == null ||
        !modelNumber?.trim() ||
        taxationYear == null ||
        !DateHelper.isValidDate(dateOfRegistration) ||
        isAnnual10KmMileageMoreThan3000 == null
      ) {
        return;
      }

      props.setIsCalculating?.(true);
      getBenefitAmount(
        annualTax,
        modelNumber.trim(),
        taxationYear,
        extraCost,
        isAnnual10KmMileageMoreThan3000,
        dateOfRegistration
      )
        .then((benefitAmount) => {
          benefitAmountConnect.setValue(benefitAmount);
        })
        .finally(() => {
          props.setIsCalculating?.(false);
        });
    },
    FieldConstants.DebounceTime
  );

  const onExtraEquipmentCostChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    extraEquipmentCostConnect.onChange(e);
    const extraCost = Number(e.target.value);
    recalculateBenefitAmount({ extraCost });
  };

  const onAnnulTaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    annualTaxConnect.onChange(e);
    const annualTax = Number(e.target.value);
    recalculateBenefitAmount({ annualTax });
  };

  const onisAnnual10KmMileageMoreThan3000Change = (checked: boolean) => {
    isAnnual10KmMileageMoreThan3000Connect.setValue(checked);
    isAnnual10KmMileageMoreThan3000Connect.setTouched(true, false);
    recalculateBenefitAmount({ isAnnual10KmMileageMoreThan3000: checked });
  };

  const onDateOfRegistrationChange = (dateValue?: any) => {
    dateOfRegistrationConnect.setValue(dateValue);
    const dateOfRegistration = dateValue as Date;
    recalculateBenefitAmount({ dateOfRegistration });
  };

  const onModelNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    modelNumberConnect.onChange(e);
    if (searchMethod === BenefitsEnums.CarBenefitSearchMethod.ModelNumber) {
      const modelNumber = e.target.value;
      recalculateBenefitAmount({ modelNumber });
    }
  };

  return (
    <>
      <VerticalFormField<ChoiceProps>
        Component={Choice}
        {...carBenefitSearchMethodConnect}
        label={TranslationKeys.Benefit.CarBenefitSearchMethodLabel}
        items={carBenefitSearchMethodChoices}
        showMainLabel
        setValue={onSearchMethodChange}
      />
      {searchMethod !== BenefitsEnums.CarBenefitSearchMethod.ModelNumber && (
        <>
          <VerticalFormField<SearchInputProps>
            Component={SearchInput}
            {...yearConnect}
            label={TranslationKeys.Benefit.CarBenefitYearLabel}
            items={yearList}
            setValue={onYearChange}
          />
          <VerticalFormField<SearchInputProps>
            Component={SearchInput}
            {...brandConnect}
            label={TranslationKeys.Benefit.CarBenefitBrandLabel}
            items={brandList}
            setValue={onBrandChange}
          />
          <VerticalFormField<SearchInputProps>
            Component={SearchInput}
            {...vehicleConnect}
            label={TranslationKeys.Benefit.CarBenefitVehicleLabel}
            descriptionText={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Benefit.CarBenefitVehicleDescription
            )}
            items={vehicleList}
            isLoading={isLoadingVehicles}
            showLoading
            disabled={isLoadingVehicles || !yearConnect.value || !brandConnect.value}
            setValue={onVehicleSelect}
          />
        </>
      )}
      <VerticalFormField<TextInputProps>
        Component={TextInput}
        {...modelNumberConnect}
        label={TranslationKeys.Benefit.CarBenefitModelNumberLabel}
        disabled={searchMethod !== BenefitsEnums.CarBenefitSearchMethod.ModelNumber}
        onChange={onModelNumberChange}
      />
      <VerticalFormField<CurrencyInputProps>
        Component={CurrencyInput}
        {...extraEquipmentCostConnect}
        label={TranslationKeys.Benefit.CarBenefitCostOfExtraEquipmentLabel}
        decimalPoints={0}
        currency={CurrencyCode.SEK}
        onChange={onExtraEquipmentCostChange}
      />
      <VerticalFormField<CurrencyInputProps>
        Component={CurrencyInput}
        {...annualTaxConnect}
        label={TranslationKeys.Benefit.CarBenefitAnnualTaxLabel}
        decimalPoints={0}
        currency={CurrencyCode.SEK}
        onChange={onAnnulTaxChange}
      />
      <HorizontalFormField<ToggleProps>
        Component={Toggle}
        {...isAnnual10KmMileageMoreThan3000Connect}
        label={TranslationKeys.Benefit.CarBenefitNo10KmDrivenInServiceLabel}
        descriptionText={LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.Benefit.CarBenefitNo10KmDrivenInServiceDescription
        )}
        setValue={onisAnnual10KmMileageMoreThan3000Change}
        mainLabelGridSize={true}
        childGridSize={"auto"}
      />
      <VerticalFormField<DateInputProps>
        Component={DateInput}
        {...dateOfRegistrationConnect}
        label={TranslationKeys.Benefit.CarBenefitDateOfRegistrationLabel}
        descriptionText={LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.Benefit.CarBenefitDateOfRegistrationDescription
        )}
        setValue={onDateOfRegistrationChange}
      />
    </>
  );
};

export default CarBenefitSection;
